/**
 * Local draft of a workflow form (BRIEFD-7598): a reload, a lost connection (the maintenance
 * redirect on status 0) or an accidental back-navigation mid-wizard used to throw away every
 * answer. Kept per user and per entity + workflow, only in this browser, and dropped once the
 * form is submitted; a draft older than a week is ignored and removed on the next load.
 */
const STORAGE_KEY_PREFIX = 'workflowDraft:';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface WorkflowDraft {
  savedAt: number;
  /** Index of the section (step) the user was on, to land them back there. */
  sectionIndex: number | null;
  /** Flat `{fieldUuid: value}` as submitted by the form. */
  values: Record<string, unknown>;
}

export function workflowDraftKey(userId: string, parts: Array<string | null | undefined>): string {
  return `${STORAGE_KEY_PREFIX}${userId}:${parts.map((part) => part ?? '').join(':')}`;
}

/**
 * Only values that survive a JSON round trip unchanged are kept: a Date or Moment would come back
 * as a string and break its date control, and it is better to leave such a field empty than wrong.
 */
export function isDraftSafeValue(value: unknown): boolean {
  if (value === null || ['string', 'number', 'boolean'].includes(typeof value)) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.every(isDraftSafeValue);
  }
  if (typeof value === 'object') {
    const proto = Object.getPrototypeOf(value);
    return (proto === Object.prototype || proto === null)
      && Object.values(value as Record<string, unknown>).every(isDraftSafeValue);
  }
  return false;
}

export function saveWorkflowDraft(key: string, sectionIndex: number | null, values: Record<string, unknown>, now: number = Date.now()): void {
  const safe: Record<string, unknown> = {};
  for (const [fieldUuid, value] of Object.entries(values)) {
    if (isDraftSafeValue(value)) {
      safe[fieldUuid] = value;
    }
  }
  try {
    localStorage.setItem(key, JSON.stringify({savedAt: now, sectionIndex, values: safe} satisfies WorkflowDraft));
  } catch (e) {
    // Storage unavailable or full (private browsing, quota): the form simply behaves as before.
  }
}

export function loadWorkflowDraft(key: string, now: number = Date.now()): WorkflowDraft | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (typeof parsed?.savedAt !== 'number' || now - parsed.savedAt > MAX_AGE_MS
      || !parsed.values || typeof parsed.values !== 'object' || Array.isArray(parsed.values)) {
      localStorage.removeItem(key);
      return null;
    }
    const values: Record<string, unknown> = {};
    for (const [fieldUuid, value] of Object.entries(parsed.values)) {
      if (isDraftSafeValue(value)) {
        values[fieldUuid] = value;
      }
    }
    return {
      savedAt: parsed.savedAt,
      sectionIndex: Number.isInteger(parsed.sectionIndex) && parsed.sectionIndex >= 0 ? parsed.sectionIndex : null,
      values,
    };
  } catch (e) {
    clearWorkflowDraft(key);
    return null;
  }
}

export function clearWorkflowDraft(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    // Nothing to clean up if storage is unavailable.
  }
}

/** Drafts hold what a user typed (possibly client data): drop every one when they log out. */
export function clearAllWorkflowDrafts(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch (e) {
    // Storage unavailable: nothing was saved either.
  }
}
